import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Trophy, Shield, Award, Users, Anchor, Target, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import UnifiedBookingCalendar from './UnifiedBookingCalendar';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export default function TeamBuilding() {
  const { assets, resolveMediaUrl } = useBrand();
  const [instructors, setInstructors] = useState<any[]>([]);

  useEffect(() => {
    const q = query(collection(db, 'instructors'));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      list.sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
      setInstructors(list);
    }, (error) => {
      console.error("Failed to load instructors list", error);
    });
    return unsub;
  }, []);

  const activeInstructors = instructors.length > 0 ? instructors : [
    {
      name: "Taras V.",
      title: "LEAD INSTRUCTOR & COACH",
      initials: "TV",
      image: "",
      achievements: [
        "Crossed Lake Ontario on iSup with no motor boat support — three times.",
        "Completed 110 km international expedition."
      ],
      credentials: [
        "🥋 Black Belt in Judo",
        "NCCP Certified Coach",
        "Safe Sport Certified",
        "Emergency Action Plan Certified",
        "Mental Health in Sport Integration"
      ]
    },
    {
      name: "Mike",
      title: "INSTRUCTOR IN TRAINING",
      initials: "M",
      image: "",
      achievements: [
        "Crossed Lake Ontario on paddle board without support boats."
      ],
      credentials: [
        "🥋 Judo Brown Belt",
        "🥋 BJJ Brown Belt"
      ]
    }
  ];

  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen relative overflow-hidden">
      <Helmet>
        <title>Corporate Team Building Toronto | Open Water Paddleboard Experience | OWS</title>
        <meta name="description" content="Corporate team building on Lake Ontario, Toronto. 4-hour guided paddleboard expedition through the Toronto Islands. Groups up to 8. All equipment provided. From $1,500 CAD. Operated by Open Water Syndicate at Queens Quay West Marina." />
        <meta name='keywords' content='corporate team building Toronto, team building activities Toronto, outdoor team building Toronto, paddleboard team building, corporate water activities Toronto, group paddleboard Toronto, unique team building Toronto, leadership team building outdoor' />
        <link rel='canonical' href='https://openwatersyndicate.com/team-building' />
      </Helmet>

      {/* Subtle Background Elements */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(18,52,59,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(18,52,59,0.1)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 text-ows-teal mb-6">
            <Trophy className="h-5 w-5" />
            <span className="text-[10px] font-bold tracking-[0.4em] uppercase font-mono">High-Performance Teams</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-display font-light uppercase tracking-tight text-ows-dark mb-8 leading-tight">
            Corporate <br />
            <span className="italic text-ows-teal font-serif lowercase tracking-normal">Expeditions.</span>
          </h1>
          <p className="text-lg md:text-xl text-ows-dark/60 max-w-3xl mx-auto font-light leading-relaxed">
            Take your team off-site and onto the water. A 4-hour guided paddleboard expedition through the Toronto Islands, led by certified open water instructors.
          </p>
        </div>

        {/* Hero Visual */}
        <div className="flex justify-center max-w-5xl mx-auto mb-24 relative group">
          <div className="absolute -inset-4 bg-gradient-to-r from-ows-teal/20 to-ows-orange/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-[3rem]"></div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full aspect-[21/9] bg-ows-dark rounded-[2rem] overflow-hidden shadow-2xl relative border border-white/20"
          >
            <img src={assets.teamBuildingCardImage ? resolveMediaUrl(assets.teamBuildingCardImage) || undefined : "https://images.unsplash.com/photo-1544551763-46a0e38eebba?auto=format&fit=crop&q=80&w=1200"} alt="Team paddle boarding" className="w-full h-full object-cover opacity-60 mix-blend-overlay group-hover:scale-105 transition-transform duration-[2s]" />
            <div className="absolute inset-0 bg-gradient-to-t from-ows-dark via-ows-dark/40 to-transparent flex flex-col justify-end p-12">
              <div className="text-white text-3xl md:text-5xl font-display uppercase tracking-widest mb-4">The CORPORATE Team Building</div>
              <p className="text-white/80 max-w-2xl font-serif italic text-lg line-clamp-2 md:line-clamp-none">
                A highly synchronized, challenging, and expertly guided mission for groups up to 8 participants. No trust falls—just absolute accountability and mutual reliance in open water conditions.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Why Open Water Builds Better Teams */}
        <div className="mb-32">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-display uppercase tracking-widest text-ows-dark mb-4">Why Open Water?</h2>
            <p className="text-ows-dark/60 font-serif italic max-w-xl mx-auto">The water strips away corporate titles. In changing wind and wave vectors, true leadership, communication, and resilience immediately rise to the surface.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-10 rounded-[2rem] border border-ows-teal/10 hover:border-ows-teal/30 hover:shadow-xl transition-all h-full">
              <div className="w-14 h-14 bg-ows-sand rounded-xl flex items-center justify-center mb-8">
                <Target className="h-6 w-6 text-ows-teal" />
              </div>
              <h3 className="text-xl font-bold uppercase tracking-widest text-ows-dark mb-4">Absolute Clarity</h3>
              <p className="text-ows-dark/60 font-light leading-relaxed">
                When physical endurance meets dynamic environments, communication must be precise and definitive. Your team will learn to issue and execute clear directives under pressure.
              </p>
            </div>

            <div className="bg-ows-dark text-white p-10 rounded-[2rem] border border-white/10 hover:border-ows-teal/50 shadow-2xl transition-all h-full scale-100 md:scale-105 z-10">
              <div className="w-14 h-14 bg-ows-teal rounded-xl flex items-center justify-center mb-8">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold uppercase tracking-widest mb-4">Collective Resilience</h3>
              <p className="text-white/70 font-light leading-relaxed mb-6">
                A team is only as fast as its slowest vessel. They must adapt, draft, and tow each other when conditions turn difficult. Success is exclusively collective.
              </p>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-ows-teal flex items-center gap-2">
                <Shield className="h-3 w-3" /> Core Competency
              </div>
            </div>

            <div className="bg-white p-10 rounded-[2rem] border border-ows-teal/10 hover:border-ows-teal/30 hover:shadow-xl transition-all h-full">
              <div className="w-14 h-14 bg-ows-sand rounded-xl flex items-center justify-center mb-8">
                <Anchor className="h-6 w-6 text-ows-teal" />
              </div>
              <h3 className="text-xl font-bold uppercase tracking-widest text-ows-dark mb-4">Shared Achievement</h3>
              <p className="text-ows-dark/60 font-light leading-relaxed">
                Completing a challenging route bonds a team unlike any office workshop. Returning to shore after commanding the horizon builds profound mutual respect and trust.
              </p>
            </div>
          </div>
        </div>

        {/* Session Overview (ROUTE 01) */}
        <div className="max-w-5xl mx-auto mb-32">
          <h3 className="text-2xl md:text-3xl font-serif uppercase tracking-widest text-[#0D3A40] mb-12 text-center">Session Overview (Route 01)</h3>
          <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-[#0D3A40]/20 before:to-transparent">

            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-[#0D3A40] shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 text-white font-bold text-xs">
                01
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 bg-white border border-[#E2E8F0] shadow-sm rounded-xl">
                <div className="mb-2">
                  <h4 className="font-bold text-[#0D3A40] text-sm uppercase tracking-widest">Phase 1: Briefing & Equipment Setup</h4>
                  <span className="text-xs text-[#0D3A40]/60">(Queens Quay West Marina)</span>
                </div>
                <div className="text-sm text-[#2D3748] leading-relaxed">
                  Technical ground school, hybrid gear configuration, and introductory breath-control mechanics.
                </div>
              </div>
            </div>

            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-[#0D3A40] shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 text-white font-bold text-xs">
                02
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 bg-white border border-[#E2E8F0] shadow-sm rounded-xl">
                <div className="mb-2">
                  <h4 className="font-bold text-[#0D3A40] text-sm uppercase tracking-widest">Phase 2: Harbour Crossing</h4>
                  <span className="text-xs text-[#0D3A40]/60">(Seated Kayak Configuration)</span>
                </div>
                <div className="text-sm text-[#2D3748] leading-relaxed">
                  Utilizing deployed kayak seats and dual-blade paddle attachments for a highly stable, zero-fear crossing through active harbor traffic.
                </div>
              </div>
            </div>

            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-[#0D3A40] shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 text-white font-bold text-xs">
                03
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 bg-white border border-[#E2E8F0] shadow-sm rounded-xl">
                <div className="mb-2">
                  <h4 className="font-bold text-[#0D3A40] text-sm uppercase tracking-widest">Phase 3: Stand-Up Progression</h4>
                  <span className="text-xs text-[#0D3A40]/60">(Toronto Island Inside Channels)</span>
                </div>
                <div className="text-sm text-[#2D3748] leading-relaxed">
                  Transitioning from seated to full stand-up paddleboarding in glassy, sheltered waters with synchronized team pace drills.
                </div>
              </div>
            </div>

            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-[#0D3A40] shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 text-white font-bold text-xs">
                04
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 bg-white border border-[#E2E8F0] shadow-sm rounded-xl">
                <div className="mb-2">
                  <h4 className="font-bold text-[#0D3A40] text-sm uppercase tracking-widest">Phase 4: The Island Portage</h4>
                  <span className="text-xs text-[#0D3A40]/60">(Gibraltar Point Beach South Shore)</span>
                </div>
                <div className="text-sm text-[#2D3748] leading-relaxed">
                  A tactical team portage across the historic island terrain to reach the raw, open south shore.
                </div>
              </div>
            </div>

            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-[#0D3A40] shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 text-white font-bold text-xs">
                05
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 bg-white border border-[#E2E8F0] shadow-sm rounded-xl">
                <div className="mb-2">
                  <h4 className="font-bold text-[#0D3A40] text-sm uppercase tracking-widest">Phase 5: Open Water Entry</h4>
                  <span className="text-xs text-[#0D3A40]/60">(Lake Ontario)</span>
                </div>
                <div className="text-sm text-[#2D3748] leading-relaxed">
                  A highly supervised, 150-meter entry into open water to test stability, posture, and composure under real marine conditions before returning to the team extraction point.
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Meet the Head Instructors */}
        <div className="mb-32">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 text-ows-teal mb-4">
              <Award className="h-5 w-5" />
              <span className="text-[10px] font-bold tracking-[0.4em] uppercase font-mono">Elite Leadership</span>
            </div>
            <h2 className="text-3xl font-display uppercase tracking-widest text-[#0D3A40] mb-4">INSTRUCTORS</h2>
            <p className="text-ows-dark/60 font-serif italic max-w-xl mx-auto">
              Our sessions are designed and led by certified open water instructors — three unsupported Lake Ontario crossings, a 110km international expedition, black belt in judo, NCCP certified coaching. We bring professional structure and safety to your team.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {activeInstructors.map((inst, idx) => (
              <div key={idx} className="bg-white p-10 rounded-[2.5rem] border border-ows-teal/10 hover:border-ows-teal/30 hover:shadow-2xl hover:shadow-ows-teal/5 transition-all duration-500 relative flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 bg-ows-dark text-white rounded-2xl flex items-center justify-center overflow-hidden border border-ows-teal/10 shadow-sm">
                      {inst.image ? (
                        <img src={resolveMediaUrl(inst.image)} alt={inst.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="font-display font-bold text-lg">{inst.initials || inst.name.substring(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold uppercase tracking-wider text-ows-dark">{inst.name}</h3>
                      <p className="text-xs text-ows-teal uppercase tracking-widest font-mono font-bold mt-1">{inst.title}</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {inst.achievements?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ows-dark/40 mb-2 font-mono">Expedition Achievements</div>
                        <ul className="space-y-3">
                          {inst.achievements.map((ach: string, aIdx: number) => (
                            <li key={aIdx} className="flex items-start gap-3 text-sm text-ows-dark/80 font-light">
                              <span className="text-ows-teal font-bold mt-0.5">•</span>
                              <span>{ach}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {inst.credentials?.length > 0 && (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-ows-dark/40 mb-2 font-mono">Professional & Martial Credentials</div>
                        <div className="flex flex-wrap gap-2">
                          {inst.credentials.map((cred: string, cIdx: number) => {
                            const isBlackBelt = cred.toLowerCase().includes('black belt');
                            return (
                              <span
                                key={cIdx}
                                className={`px-3 py-1.5 text-[9px] font-mono tracking-wider uppercase rounded-md font-bold leading-none ${isBlackBelt
                                  ? "bg-[#141414] text-white border border-white/10 shadow-sm"
                                  : "bg-ows-sand text-ows-dark border border-ows-teal/15"
                                  }`}
                              >
                                {cred}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing / Booking Details */}
        <div className="bg-ows-sand/20 rounded-[3rem] p-12 md:p-16 mb-24 border border-ows-teal/10 max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-3xl font-display uppercase tracking-widest text-ows-dark mb-4">The Standard Package</h3>
            <p className="text-ows-dark/70 font-light mb-8 max-w-md">Our base corporate rate covers everything required for a flawless, intensive 4-hour expedition with premium outfitting.</p>
            <ul className="space-y-4">
              <li className="flex items-center justify-center md:justify-start gap-3 text-sm tracking-wide font-bold uppercase text-ows-dark"><Shield className="h-4 w-4 text-ows-teal" /> Private 4-hour exclusive deployment</li>
              <li className="flex items-center justify-center md:justify-start gap-3 text-sm tracking-wide font-bold uppercase text-ows-dark"><Shield className="h-4 w-4 text-ows-teal" /> Premium high-performance equipment</li>
              <li className="flex items-center justify-center md:justify-start gap-3 text-sm tracking-wide font-bold uppercase text-ows-dark"><Shield className="h-4 w-4 text-ows-teal" /> 8 Participants Maximum per team</li>
              <li className="flex items-center justify-center md:justify-start gap-3 text-sm tracking-wide font-bold uppercase text-ows-dark"><Shield className="h-4 w-4 text-ows-teal" /> Digital Media & Debrief Briefing</li>
            </ul>
          </div>
          <div className="bg-white p-10 rounded-3xl text-center border border-ows-teal/20 shadow-xl min-w-[300px]">
            <div className="text-5xl font-mono uppercase tracking-widest mb-2 text-ows-dark">$1,500</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-ows-dark/40 mb-8">Flat Rate Per Group</div>
            <button
              onClick={() => document.getElementById('team-calendar')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full bg-ows-dark text-white px-8 py-4 rounded-full font-bold uppercase text-xs tracking-widest hover:bg-ows-teal transition-colors flex justify-center items-center gap-3"
            >
              Book Your Team <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Corporate Booking Only Calendar */}
        <div id="team-calendar" className="mt-32 pt-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-display uppercase tracking-widest text-ows-dark mb-4">Availability Calendar</h2>
            <p className="text-ows-dark/60 font-serif italic max-w-xl mx-auto"> Select a date for your team. The entire slot is locked exclusively for your team.</p>
          </div>
          <UnifiedBookingCalendar
            type="team-building"
          />
        </div>

      </div>
    </div>
  );
}
